/**
 * W1-TODAY-HERO (TH-7, TH-8, TH-9) — the 2.0 hero's action area: ONE primary
 * action, chosen from facts the hero reads itself.
 *
 *   - Native app AND `run_capture_v1` on AND today not done: "Start run" (a link to
 *     /run), its helper line, and the quiet "Ran it already? Mark done" under it.
 *   - Everything else (the web, or run capture off): the "Mark done" button.
 *   - Today done: "Done · Tap to undo" with the effort action beside it, and the
 *     undo question when something is logged.
 *
 * Both completion controls are the real `MarkTodayCompleteButton`, so "Ran it
 * already? Mark done" and "Mark done" are the same handler (`onToggle`, which
 * LoopHero binds to `onToggleDay`): one write per press, whichever control it was.
 *
 * The button is always the second child of the container, so React keeps the one
 * instance (its pending state and its focus) when the day flips from not done to
 * done and the Start run link goes away.
 */
import type { ReactNode } from 'react';
import type { Day } from '../../../fixtures/plan';
import { useServerFlag } from '../../../hooks/useServerFlag';
import { isNativeApp } from '../../../lib/platform';
import { MarkTodayCompleteButton } from '../MarkTodayCompleteButton';
import { StartRunBlock } from './StartRunBlock';
import styles from './HeroActionsV2.module.css';

export interface HeroActionsV2Props {
  day: Day;
  onToggle: (dayId: string, currentStatus: Day['completionStatus']) => Promise<unknown>;
  /** The effort action, shown beside "Done · Tap to undo" (hidden while the undo question is open). */
  effortAction?: ReactNode;
}

export function HeroActionsV2({ day, onToggle, effortAction }: HeroActionsV2Props) {
  const captureOn = useServerFlag('run_capture_v1');
  const startRun = isNativeApp() && captureOn && day.completionStatus !== 'completed';
  return (
    <div className={styles.actions}>
      {startRun && <StartRunBlock />}
      <MarkTodayCompleteButton
        variant="v2"
        markStyle={startRun ? 'text' : 'primary'}
        day={day}
        onToggle={onToggle}
        trailing={effortAction}
      />
    </div>
  );
}
